/**
 * @fileoverview Authentication API service
 * Handles all authentication-related HTTP requests using existing API infrastructure
 * @author ISCRI Team
 */

import { apiPost, apiGet, handleApiError } from "@services/api/apiConfig";

/**
 * Authentication API endpoints
 * @constant {Object}
 */
const AUTH_ENDPOINTS = {
  LOGIN: "/login",
  LOGOUT: "/logout",
  REFRESH: "/refresh",
  VERIFY: "/verify",
  PROFILE: "/profile",
};

/**
 * Authentication API methods
 */
export const authApi = {
  /**
   * User login request
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response with user data and tokens
   * @throws {Error} Authentication error with user-friendly message
   */
  login: async (credentials) => {
    try {
      const response = await apiPost(AUTH_ENDPOINTS.LOGIN, credentials);

      if (!response.user || !response.user.id) {
        throw new Error("Invalid server response: missing user data");
      }

      return {
        user: {
          id: response.user.id,
          username: response.user.username || response.user.name,
          email: response.user.email,
        },
        accessToken: response.accessToken || response.access_token,
        refreshToken: response.refreshToken || response.refresh_token,
        expiresIn: response.expiresIn || response.expires_in || 7200,
      };
    } catch (error) {
      handleApiError(error, "User login");
    }
  },

  /**
   * User logout request
   * @returns {Promise<Object>} Logout response
   * @throws {Error} Logout error with user-friendly message
   */
  logout: async () => {
    try {
      const response = await apiPost(AUTH_ENDPOINTS.LOGOUT);

      return {
        success: response.success !== false,
        message: response.message || "Logged out successfully",
      };
    } catch (error) {
      handleApiError(error, "User logout");
    }
  },

  /**
   * Refresh authentication token
   * @returns {Promise<Object>} Refresh response with new tokens
   * @throws {Error} Token refresh error with user-friendly message
   */
  refreshToken: async () => {
    try {
      const response = await apiPost(AUTH_ENDPOINTS.REFRESH);

      if (!response.accessToken && !response.access_token) {
        throw new Error("Invalid server response: missing access token");
      }

      return {
        accessToken: response.accessToken || response.access_token,
        refreshToken: response.refreshToken || response.refresh_token,
        expiresIn: response.expiresIn || response.expires_in || 7200,
      };
    } catch (error) {
      handleApiError(error, "Token refresh");
    }
  },

  /**
   * Verify current authentication status
   * @returns {Promise<Object>} User verification response
   * @throws {Error} Verification error with user-friendly message
   */
  verifyAuth: async () => {
    try {
      const response = await apiGet(AUTH_ENDPOINTS.VERIFY);

      if (!response.user || !response.user.id) {
        throw new Error("Authentication verification failed");
      }

      return {
        user: {
          id: response.user.id,
          username: response.user.username || response.user.name,
          email: response.user.email,
        },
        isValid: response.valid !== false,
        expiresAt: response.expiresAt || response.expires_at,
      };
    } catch (error) {
      handleApiError(error, "Authentication verification");
    }
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   * @throws {Error} Profile fetch error with user-friendly message
   */
  getProfile: async () => {
    try {
      const response = await apiGet(AUTH_ENDPOINTS.PROFILE);

      if (!response.user || !response.user.id) {
        throw new Error("Failed to fetch user profile");
      }

      return {
        user: {
          id: response.user.id,
          username: response.user.username || response.user.name,
          email: response.user.email,
          createdAt: response.user.createdAt || response.user.created_at,
          lastLogin: response.user.lastLogin || response.user.last_login,
          permissions: response.user.permissions || [],
          roles: response.user.roles || [],
        },
      };
    } catch (error) {
      handleApiError(error, "User profile fetch");
    }
  },
};

/**
 * Authentication-specific error types for enhanced error handling
 * @constant {Object}
 */
export const AUTH_ERRORS = {
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  TOKEN_INVALID: "TOKEN_INVALID",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  PERMISSION_DENIED: "PERMISSION_DENIED",
};

/**
 * Parse authentication-specific API errors
 * @param {Error} error - Original error from API
 * @returns {Object} Enhanced error object with auth-specific context
 */
export const parseAuthError = (error) => {
  const message = error.message?.toLowerCase() || "";

  if (message.includes("invalid credentials") || message.includes("401")) {
    return {
      type: AUTH_ERRORS.INVALID_CREDENTIALS,
      message: "Invalid email or password",
      originalError: error,
    };
  }

  if (
    message.includes("session expired") ||
    message.includes("token expired")
  ) {
    return {
      type: AUTH_ERRORS.SESSION_EXPIRED,
      message: "Your session has expired. Please log in again.",
      originalError: error,
    };
  }

  if (
    message.includes("invalid token") ||
    message.includes("malformed token")
  ) {
    return {
      type: AUTH_ERRORS.TOKEN_INVALID,
      message: "Authentication token is invalid. Please log in again.",
      originalError: error,
    };
  }

  if (
    message.includes("account locked") ||
    message.includes("account suspended")
  ) {
    return {
      type: AUTH_ERRORS.ACCOUNT_LOCKED,
      message: "Your account has been locked. Please contact support.",
      originalError: error,
    };
  }

  if (message.includes("403") || message.includes("forbidden")) {
    return {
      type: AUTH_ERRORS.PERMISSION_DENIED,
      message: "You do not have permission to perform this action.",
      originalError: error,
    };
  }

  // Fallback to generic error parsing from apiConfig
  return {
    type: "UNKNOWN_AUTH_ERROR",
    message: error.message || "An authentication error occurred",
    originalError: error,
  };
};

/**
 * Check if error is authentication-related and requires re-login
 * @param {Error} error - Error to check
 * @returns {boolean} Whether error requires authentication
 */
export const requiresReAuthentication = (error) => {
  const authError = parseAuthError(error);

  return [
    AUTH_ERRORS.INVALID_CREDENTIALS,
    AUTH_ERRORS.SESSION_EXPIRED,
    AUTH_ERRORS.TOKEN_INVALID,
  ].includes(authError.type);
};